import { createTheme } from "@mui/material/styles";
import zIndex from "@mui/material/styles/zIndex";
import { alpha, fontSize, minWidth } from "@mui/system";

const baseTheme = {
  typography: {
    fontFamily: "Roboto, Arial, sans-serif",
    h1: {
      // fontSize: "28px",
      fontSize: "1.75rem",
      fontWeight: 600,
      lineHeight: "39.2px",
    },
    h2: {
      // fontSize: "20px",
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: "28px",
    },
    body1: {
      // fontSize: "16px",
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: "22.4px",
    },
    body2: {
      // fontSize: "14px",
      fontSize: "0.875rem",
      fontWeight: 500,
      lineHeight: "18.2px",
    },
    caption: {
      // fontSize: "12px",
      fontSize: "0.75rem",
      fontWeight: 500,
      lineHeight: "15.6px",
    },
    title: {
      // fontSize: "20px",
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: "15.6px",
    },
    sub_title: {
      // fontSize: "14px",
      fontSize: "0.875rem",
      fontWeight: 600,
      lineHeight: "130%",
    },

    item_card_title: {
      // fontSize: "14px",
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: "130%",
    },

    item_card_price: {
      // fontSize: "14px",
      fontSize: "0.875rem",
      fontWeight: 500,
      lineHeight: "130%",
    },
  },
  breakpoints: {
    values: {
      xs: 320, // extra-small screens (phones)
      sm: 600, // small screens (tablets)
      md: 960, // medium screens (small laptops/desktops)
      lg: 1280, // large screens (laptops/desktops)
      xl: 1920, // extra-large screens (larger desktops)
    },
  },
  components: {
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 0,
          paddingTop: 14,
          borderRadius: "16px",
          "&:last-child": {
            paddingBottom: 0,
          },
        },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: "red",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          background: "",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          // boxShadow: "var(--card-shadow)",
        }),
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          // boxShadow: "var(--card-shadow)",
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }) => ({
          "& .MuiOutlinedInput-root": {
            backgroundColor: theme.palette.input_field.fill,
          },
        }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: ({ theme }) => ({
          "& .MuiOutlinedInput-root": {
            backgroundColor: theme.palette.input_field.fill,
          },
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: ({ theme }) => ({
          "& .MuiOutlinedInput-root": {
            backgroundColor: theme.palette.input_field.fill,
          },
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.background.default,
          // boxShadow: "var(--drawer-shadow)",
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "capitalize",
        },
      },
    },
  },

  shape: {
    borderRadius: 4,
  },
};

const lightTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: "light",
    primary: {
      main: "#0077B6", //primary color light
    },
    secondary: {
      main: "#6495ED", //primary color light
    },
    background: {
      // default: "#F6F6F6",
      // paper: "#FAFAFF",
      // tertiary: "#EFEFEF",

      // second light blue
      // default: "#F2F8FF",
      // paper: "#F8FCFF",
      // tertiary: "#EFEFEF",

      // default: "#fafafa", f8f9ff
      // paper: "#f5f5f5",
      // tertiary: "#f8f8f8",

      default: "#f3f5f7",
      paper: "#fafafa",
      tertiary: "#f8f8f8",

      // default: "#f3f0f0",
      // paper: "#fbfbfb",

      fill: "#FAFAFF",
    },
    text: {
      primary: "#1F1D2B",
      secondary: "#5C5C5C",
      tertiary: "#747474",
      icons: "#7C7B7B",
      buttontxt: "#ffffff",
    },
    stroke: {
      main: "#D7D7D7",
    },
    input_field: {
      // fill: "#FAFAFF",
      // stroke: "#D7D7D7",
      // text: "#747474",

      fill: "#FBFDFF",
      stroke: "#BAD6F2",
      text: "#747474",
    },

    system: {
      danger: "#FF3030",
      warning: "#FF8C00",
      info: "#0CA4FF",
    },
  },
});

const darkTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: "dark",
    primary: {
      main: "#EA7C69", // primary color dark
    },
    background: {
      default: "#1F1D2B",
      paper: "#252836",
      tertiary: "#393C49",
      fill: "#252836",
    },
    text: {
      primary: "#D9D9D9",
      secondary: "#9D9D9D",
      tertiary: "#5C5C5C",
      icons: "#6C6C6C",
      buttontxt: "#ffffff",
    },
    stroke: {
      main: "#ABBBC2",
    },
    input_field: {
      fill: "#2D303E",
      stroke: "#393C49",
      text: "#ABBBC2",
    },
    system: {
      danger: "#FF7CA3",
      warning: "#FF8C00",
      info: "#0466c8",
    },
  },
});

const useStyles = (theme) => ({
  width: { width: "100%" },

  height: { height: "100%" },

  progress_circular: {
    // width: "100%",
    // height: "100vh",
    mx: "50%",
    my: "25%",
    // display: "flex",
    // justifyContent: "center",
    // alignItems: "center",
  },

  btn_bootom_align: {
    position: "absolute",
    bottom: 0,
    mb: 2,
    left: 0,
    right: 0,
    padding: "0 16px",
    width: "100%",
  },

  btn_group_bootom_align: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "start",

    // position: "absolute",
    // bottom: 0,
    // mb: 2,
    // right: 0,
    // padding: "0 16px",
  },

  btn_primary: {
    width: "100%",
    textAlign: "center",
    justifyContent: "center",
    textTransform: "capitalize",
    color: theme.palette.text.buttontxt,
    textWrap: "nowrap",

    boxShadow: 1,
    // padding: "4px 8px",
    background: theme.palette.primary.main,
  },

  btn_primary_2: {
    width: "100%",
    textAlign: "center",
    justifyContent: "center",
    textTransform: "capitalize",
    color: theme.palette.text.buttontxt,
    textWrap: "nowrap",

    boxShadow: 1,
    // padding: "4px 8px",
    background: alpha(theme.palette.primary.main, 0.8),
  },

  btn_secondary: {
    width: "100%",
    textAlign: "center",
    justifyContent: "center",
    textTransform: "capitalize",
    textWrap: "nowrap",
    // padding: "4px 8px",
    background: theme.palette.input_field.fill,
    boxShadow: 1,
  },

  // header primary start (cashier,kitchen/place order)
  header_grid_container: {
    // flexGrow: 1,
    paddingBottom: "16px",
    // borderBottom: `2px solid ${theme.palette.background.paper}`,
  },

  header_title: {
    color: theme.palette.text.primary,
    // fontSize: theme.typography.h1,
    fontSize: theme.typography.h2,
    fontWeight: "600",

    "@media (max-width: 600px)": {
      fontSize: theme.typography.sub_title,
    },
  },

  header_date: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption,
    paddingTop: "2px",
  },

  header_search_pr: {
    display: "flex",
    alignItems: "end",

    justifyContent: {
      xs: "center",
      sm: "center",
      md: "center",
      lg: "flex-end",
      xl: "flex-end",
    },
  },

  AddStyledBox: {
    background: theme.palette.background.default,
    // borderRadius: "4px",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    borderRadius: 2,
    // mt: 18,
    mt: { xs: 22, sm: 16, md: 16, lg: 18, xl: 18 },
    // ml: 13,
    ml: { xs: 0, sm: 0, md: 13 },
    mr: { xs: 0, sm: 0, md: 4 },
    // mr: 4,
  },

  aside_arrow_icon: {
    borderRadius: "8px",
    border: "1 px solid",
    border: `1px solid ${theme.palette.input_field.stroke}`,
    background: theme.palette.input_field.fill,
    display: "flex",
    justifyContent: "center",
    padding: "8px",
    height: "5%",
    position: "absolute",
    left: "-20px",
    top: "50%",
    transition: "transform 0.3s ease",
    zIndex: "1000000",
  },

  // header primary start (cashier,kitchen/place order)

  root: {
    flexGrow: 1,
    height: "100%",
    overflow: "auto",
    // Width: "100%",
    ml: { xs: 0, sm: 0, md: 10 },
    // ml: theme.breakpoints.down("md") ? "16px" : "0",
    position: "fixed",
    bottom: 0,
    right: 0,
    left: 0,
    // background: theme.palette.background.tertiary,
    "&::-webkit-scrollbar": {
      width: "0.2em",
    },
    "&::-webkit-scrollbar-track": {
      background: theme.palette.input_field.text,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.input_field.stroke,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: theme.palette.background.tertiary,
    },
  },

  no_arrows: {
    "& input[type=number]": {
      "-moz-appearance": "textfield", // Firefox
    },
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
      {
        "-webkit-appearance": "none", // Chrome, Safari, Edge
        margin: 0,
      },
  },

  screen_content: {
    // padding: "12px 16px",
    // height: "100%",
    // overflow: "auto",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",

    "&::-webkit-scrollbar": {
      width: "0.2em",
    },
    "&::-webkit-scrollbar-track": {
      background: theme.palette.input_field.text,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.input_field.stroke,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: theme.palette.background.tertiary,
    },
  },
  tabs_container: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    // backgroundColor: theme.palette.background.default,
    // borderBottom: `2px solid ${theme.palette.stroke.main}`,
    position: "sticky",
    top: 0,
    // mt: 1,
    textTransform: "capitalize",
    // zIndex: 1000,
    justifyContent: "space-between",
  },
  tabs_list: {
    display: "flex",
    overflow: "auto",
    alignItems: "center",
  },
  tab_fixed: {
    minWidth: "0px",
    // width: "auto",
    flexGrow: 1,
    fontWeight: theme.typography.fontWeightRegular,
  },
  tab_selected: {
    minWidth: "0px",
    // width: "auto",
    flexGrow: 1,
    // fontWeight: theme.typography.fontWeightBold,
    backgroundColor: theme.palette.action.selected,
  },
  tab: {
    minWidth: "auto",
    width: "auto",
    flexGrow: 1,
  },
  // cashier screen start
  cs_card: {
    padding: 1,
    paddingTop: 4,
    background: "none",
    pl: 4,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  cs_card_actions: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 4,
    // boxShadow: 2,

    // borderRadius: "16px",
    // padding: "16px",
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
  },
  cs_card_media: {
    backgroundColor: theme.palette.background.tertiary,
    width: "110px",
    height: "110px",
    objectFit: "contain",
    maxWidth: "100%",
    // height: "auto",
    padding: "10px",
    position: "absolute",
    // left: "25%",
    marginTop: "-6rem",
    // marginRight: 16,
    marginRight: { xs: 22, sm: 22, md: 16, lg: 12, xl: 12 },
    borderRadius: 50,
    zIndex: 100,
    // boxShadow: `0px 0px 0.5px 0.5px ${theme.palette.input_field.stroke}`,
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
  },
  cs_card_content: {
    backgroundColor: theme.palette.background.paper,
    paddingTop: 8,
    pb: 1,
    borderRadius: 4,
    width: "100%",
    boxShadow: `0px 0px 0.8px 0.8px ${theme.palette.input_field.stroke}`,
  },

  // cashier screen category start

  category_list: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: "8px",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
  },
  category_list_title: {
    borderBottom: `1px solid ${theme.palette.stroke.main}`,
    color: theme.palette.text.secondary,
    fontSize: "18px",
    fontWeight: 600,
    padding: "4px 0",
  },

  category_list_close_icon: {
    // backgroundColor: theme.palette.background.default,
    position: "absolute",
    // zIndex: 100,
    right: 0,
    top: 0,
    borderRadius: 1,
    color: theme.palette.text.icons,
    // boxShadow: `0px 0px 2px 0px ${theme.palette.input_field.stroke}`,
  },

  category_list_items: {
    textAlign: "center",
    borderRadius: "8px",
    // fontSize: theme.typography.caption,
    fontSize: "4px",
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    boxShadow: `0px 0px 2px 0px ${theme.palette.input_field.stroke}`,
    minWidth: "120px",
  },

  // cashier screen category start

  // cashier screen end

  text_center: {
    textAlign: "center",
  },

  mp_box_container: {
    // mt: 18,
    // ml: 13,
    // pr: 4,

    mt: { xs: 16, sm: 16, md: 18, lg: 18, xl: 18 },
    ml: { xs: 0, sm: 0, md: 13, lg: 13, xl: 13 },
    pr: { xs: 0, sm: 0, md: 4, lg: 4, xl: 4 },
  },

  header_tab_popup_icon: {
    borderRadius: 1,
    boxShadow: 1,
    border: `1px solid ${theme.palette.text.secondary}`,
    background: theme.palette.input_field.fill,
    // display: "flex",
    // justifyContent: "center",
    // alignContent: "flex-end",
    padding: "4px",
    color: theme.palette.text.secondary,
  },

  cards_container: {
    backgroundColor: theme.palette.background.default,
    padding: "16px 16px",
    "&::-webkit-scrollbar": {
      width: "0.3em",
    },
    "&::-webkit-scrollbar-track": {
      background: theme.palette.input_field.text,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.input_field.stroke,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      // background: theme.palette.background.tertiary,
    },
    flexGrow: 1,
    overflowY: "auto",
    // padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    // pr: "12px",
    // pl: "4px",
    // mt: 2,
  },

  // Cashier Screen Summary Start

  summary_bill_expand_icon: {
    background: theme.palette.background.default,
    boxShadow: `0px 0px 2px 0px ${theme.palette.text.primary}`,
    borderRadius: 1,
    padding: "0 16px",
    // border: `1px solid ${theme.palette.input_field.text}`,
    "&:hover": {
      // color: theme.palette.text.buttontxt,
      backgroundColor: theme.palette.background.paper,
    },
    top: -12.5,
  },

  left_align: {
    display: "flex",
    justifyContent: "left",
    // alignItems: "flex-start",
  },
  right_align: {
    display: "flex",
    justifyContent: "right",
  },

  cs_summary_items_area: {
    // mt: 1,
    pr: 1,
    pl: 1,
    maxHeight: "32vh",
    overflow: "auto",
    // pb: 1,
    borderRadius: 1,
    mt: 1,
    mb: 1,
    background: theme.palette.background.tertiary,
    "&::-webkit-scrollbar": {
      width: "0.2em",
    },
    "&::-webkit-scrollbar-track": {
      background: theme.palette.input_field.text,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.input_field.stroke,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: theme.palette.background.tertiary,
    },
  },
  cs_summary_items_area_title_bar: {
    display: "flex",
    // justifyContent: "space-between",

    justifyContent: "flex-end",
    paddingTop: 2,
    // position: "sticky",
    // top: 0,
    background: theme.palette.background.paper,
    width: "100%",
    zIndex: 99,
  },

  // cs_summary_card: {
  //   borderRadius: 2,
  //   p: 1,
  //   background: theme.palette.input_field.fill,
  //   boxShadow: `0px 0px 2px 0px ${theme.palette.input_field.fill}`,
  //   display: "flex",
  //   justifyContent: "center",
  //   alignItems: "center",
  // },

  cs_summary_card_content: {
    display: "flex",
    justifyContent: "space-between",
    Width: "100%",
    alignItems: "center",
    borderRadius: 1,
    p: 1,
    background: theme.palette.input_field.fill,
    // boxShadow: `4px 4px 4px 4px ${theme.palette.input_field.fill}`,
    boxShadow: 2,
  },

  summary_menu_icon: {
    borderRadius: "8px",
    border: "1 px solid",
    borderColor: theme.palette.input_field.stroke,
    background: theme.palette.input_field.fill,
    display: "flex",
    justifyContent: "center",
    padding: "8px",
    // boxShadow: "0px 0px 8px 0px rgba(0, 0, 0, 0.20)",
    boxShadow: `0px 0px 2px 0px ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
  },

  cs_summary_refresh_icon: {
    borderRadius: 1,
    boxShadow: 1,
    border: `1px solid ${theme.palette.primary.main}`,
    background: theme.palette.input_field.fill,
    color: theme.palette.primary.main,
    display: "flex",
    justifyContent: "center",
    padding: "8px",
    // boxShadow: "0px 0px 8px 0px rgba(0, 0, 0, 0.20)",
  },

  cs_summary_card_id: {
    color: theme.palette.input_field.text,
    fontSize: "12px",
    fontWeight: 400,
  },

  cs_summary_card_name: {
    color: theme.palette.text.primary,
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "140%",
  },

  cs_summary_card_item_price: {
    color: theme.palette.input_field.text,
    fontSize: "12px",
    fontWeight: 700,
    lineHeight: "140%",
  },

  cs_summary_card_total_price: {
    color: theme.palette.text.primary,
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: "140%",
  },
  headerItem: {
    textAlign: "left",
  },

  cs_summary_bill: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 1,
    paddingBottom: "2px",
    borderBottom: `0.4px dashed ${theme.palette.text.icons}`,
  },

  po_summary_bill: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    py: 1,
    borderBottom: `1px solid ${theme.palette.text.icons}`,
  },

  cs_summary_description: {
    color: theme.palette.input_field.text,
    fontSize: theme.typography.body2,
  },

  cs_summary_price: {
    color: theme.palette.text.primary,
    fontSize: theme.typography.body2,
    fontWeight: "600",
  },

  cs_aside: {
    // position: "relative",
    zIndex: 100,
    transition: "margin-left 0.3s ease",
    height: "100%",

    width: "100%",

    display: "flex",
    flexDirection: "column",

    // position: "fixed",
    // top: 0,
    // bottom: 0,
    // right: "100",
    // left: "100%",
  },

  cs_aside_toggler_icon: {
    position: "absolute",
    top: "50%",
    right: "97%",
    transform: "translateY(-50%)",
    zIndex: 1300,
    background: theme.palette.background.paper,
    boxShadow: `0px 0px 2px 0px ${theme.palette.text.primary}`,
    // borderRadius: "20px 0px 0px 20px",
    borderRadius: 1,
    padding: "4px",
    height: "80px",
    "&:hover": {
      // color: theme.palette.text.buttontxt,
      backgroundColor: theme.palette.background.default,
    },
    // border: `1px solid ${theme.palette.input_field.text}`,
  },

  // cs_aside_drawer: {
  //   "& .MuiDrawer-paper": {
  //     width: "33%",
  //     height: "100%",
  //     boxSizing: "border-box",
  //     backgroundColor: theme.palette.background.paper,
  //     boxShadow: `0px 0px 2px 0px ${theme.palette.input_field.stroke}`,
  //   },
  // },
  cs_aside_drawer: {
    width: "100%",
    flexShrink: 0,
    [`& .MuiDrawer-paper`]: {
      width: "33%",
      boxSizing: "border-box",
    },
  },

  // Cashier Screen Summary End

  // Kitchen Screen  Start

  ks_card: {
    Width: "100%",
    backgroundColor: theme.palette.background.paper,
    padding: "16px 12px",
    borderRadius: "8px",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
  },

  ks_card_media: {
    width: "auto",
    maxWidth: "120px",
    minWidth: "110px",
    height: "100%",
    alignSelf: "center",
    padding: 1,
    marginLeft: "auto",
    marginRight: "auto",
    objectFit: "contain",
    background: theme.palette.background.default,
    borderRadius: "10%",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
  },
  ks_card_media_container: {
    width: "100%",

    height: "110px",
    display: "flex",
    borderLeft: `4px solid #28B411`,
  },
  ks_card_content: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "start",
  },
  ks_card_item: {
    paddingTop: "24px",
  },

  ks_card_texts: {
    color: theme.palette.text.primary,
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "130%",
    display: "flex",
    alignItems: "center",
    gap: 1,
  },

  ks_card_icons: {
    padding: "0",
    color: theme.palette.input_field.text,
  },

  ks_card_text_numbers: {
    color: theme.palette.text.primary,
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: "140%",
    display: "flex",
    alignItems: "center",
    gap: 1,
  },

  ks_card_id: {
    color: theme.palette.text.primary,
    fontSize: theme.typography.caption,
  },

  ks_card_note: {
    color: theme.palette.input_field.text,
    fontSize: theme.typography.caption,
    fontStyle: "italic !important",
  },

  aside_content: {
    width: "100%",
    height: "100vh",
    padding: "24px 16px 16px 16px",
    background: theme.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    // justifyContent: "space-between",

    "&::-webkit-scrollbar": {
      width: "0.2em",
    },
    "&::-webkit-scrollbar-track": {
      background: theme.palette.input_field.text,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.input_field.stroke,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: theme.palette.background.tertiary,
    },
  },

  aside_content_2: {
    padding: "24px 16px",
    background: theme.palette.background.paper,
    justifyContent: "center",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    height: "100%",
    overflow: "auto",
  },

  aside_content_header: {
    borderBottom: `1px solid ${theme.palette.stroke.main}`,
    paddingBottom: "8px",
    // height: "6.5vh",
    display: "flex",
    alignItems: "center",
  },

  ks_stack: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingLeft: " 12px",
    borderRadius: "4px",
    background: theme.palette.background.default,
    pt: 1,
    pb: 1,
  },

  ks_qty_count: {
    // height: "5vh",

    // border: `2px solid ${theme.palette.primary.main}`,
    // borderRadius: "50px",
    // background: theme.palette.background.paper,
    // textAlign: "center",
    // pr: 1,
    // py: "7px",

    // color: theme.palette.text.primary,

    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: 40,
    height: 40,
    borderRadius: "50%",
    border: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 1,
  },

  ks_textfield: {
    py: "5px",
    // background: theme.palette.input_field.stroke,

    "& .MuiOutlinedInput-root": {
      color: theme.palette.primary.main,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
        borderWidth: "2px",
      },
      "& .MuiInputLabel-outlined": {
        color: theme.palette.primary.main,
        // fontWeight: "bold",
      },
      "& .MuiInputBase-input.Mui-disabled": {
        color: theme.palette.primary.main,
      },
    },
  },

  // Kitchen Screen  End

  // Placed Order Screen  Start

  po_card: {
    Width: "100%",
    backgroundColor: theme.palette.background.paper,
    padding: "8px 12px",
    borderRadius: "8px",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
  },

  po_card_media: {
    width: "100%",
    padding: " 0 80px",
    borderLeft: `4px solid #28B411`,
  },
  po_card_content: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
  },
  po_card_item: {
    paddingTop: "24px",
  },

  po_card_texts: {
    color: theme.palette.text.primary,
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: "130%",
    display: "flex",
    alignItems: "center",
    gap: 1,
  },

  po_card_icons: {
    padding: "0",
    color: theme.palette.input_field.text,
  },

  po_card_text_numbers: {
    color: theme.palette.text.primary,
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "130%",
    display: "flex",
    alignItems: "center",
    gap: 1,
  },

  po_card_id: {
    color: theme.palette.text.primary,
    fontSize: theme.typography.caption,
  },

  po_card_note: {
    color: theme.palette.input_field.text,
    fontSize: theme.typography.caption,
    fontStyle: "italic !important",
  },

  po_card_grid: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexDirection: "column",
    flex: "1 0 0",
    alignSelf: "stretch",
    gap: "8px",
  },

  cheff_card_grid: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",

    flex: "1 0 0",
    alignSelf: "stretch",
    gap: "8px",
  },
  // po_summary_bill: {
  //   display: "flex",
  //   justifyContent: "space-between",
  //   alignItems: "flex-start",
  //   paddingTop: 1,
  // },

  // Placed Order Screen  End

  // Cashier Payment  Start

  cs_payment_way_options: {
    display: "flex",
    // justifyContent: "space-between",
    // Width: "100%",
  },

  cs_payment_way_options_btn: {
    height: "64px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "8px",
    Width: "100%",

    "&.Mui-selected": {
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },

  cp_main_tabs: {
    "& button": {
      // mx: 1,
      // backgroundColor: theme.palette.background.default,
      // my: 1,
      // borderRadius: 2,
    },
  },

  tabs: {
    "& button": {
      mx: 1,
      backgroundColor: theme.palette.background.default,
      my: 1,
    },

    "& button[aria-selected='false']": {
      borderRadius: "1px",
      border: `1px solid ${theme.palette.input_field.text}`,
      boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    },

    "& button[aria-selected='true']": {
      borderRadius: "4px",
      border: `2px solid ${theme.palette.primary.main}`,
      boxShadow: `0px 0px 4px 0px ${theme.palette.primary.main}`,
      boxShadow: 4,
    },
  },

  // Cashier Payment    End

  // Cashier Payment Cash Start
  cs_cash: {
    // display: "flex",
    // width: "100%",
    paddingTop: "24px",
    // padding: "24px 16px",

    background: theme.palette.background.paper,
  },

  border_bottom: {
    // paddingBottom: "12px",
    borderBottom: `1px solid ${theme.palette.stroke.main}`,
  },

  cs_cash_delete_icon: {
    borderRadius: "8px",
    border: "1 px solid",
    border: `1px solid ${theme.palette.system.danger}`,
    // background: theme.palette.background.paper,
    background: theme.palette.input_field.fill,
    color: theme.palette.system.danger,
    display: "flex",
    justifyContent: "center",
    padding: "8px",
    boxShadow: 1,
  },

  ks_cash: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    pl: 2,
    borderRadius: "8px",
    background: theme.palette.background.default,
    // border: "1px solid black",
    border: `1px solid ${theme.palette.input_field.stroke}`,
  },

  // Cashier Payment Cash End

  //Thusara
  // Navbar
  nv_CustomAppBar: {
    background: theme.palette.background.default,
    minHeight: "10%", // Set your desired height
    marginLeft: "20px",
    position: "fixed",

    elevation: 0,
  },

  nv_CustomToolbar: {
    marginLeft: 2,
  },

  nv_CustomTypography1: {
    color: theme.palette.text.primary,

    fontSize: "24px",
    fontWeight: 700,
    mt: -3,
    ml: 8,
  },

  nv_CustomTypography2: {
    color: theme.palette.text.primary,
    fontSize: "12px",
    mt: 4,
    ml: -20.5,
  },
  nv_CustomTypography3: {
    color: theme.palette.text.primary,
    fontSize: "14px",
    textAlign: "left",
    mt: 7,
    ml: -29.6,
  },

  nv_CustomBox: {
    flexGrow: 1,
  },
  nv_CustomMenu: {
    marginTop: "30px",
  },
  nv_Navbartooltip: {
    margin: 0, // Remove any default margin
    padding: 0, // Remove any default padding
  },

  nv_avatar: {
    width: 39, // Adjust size as needed
    height: 34,
    // Adjust size as needed
  },
  nv_userContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  nv_userName: {
    fontSize: "0.8rem",
    color: theme.palette.primary.main,
    padding: 2,

    // Adjust size as needed
  },
  Muiswitch: {
    width: 72,
    height: 44,

    marginLeft: 0.5,
    // position: "fixed",
    zIndex: 99999,
    // top: "94%",

    "& .MuiSwitch-switchBase": {
      margin: 1,
      padding: 0,
      transform: "translateX(6px)",
      "&.Mui-checked": {
        color: "#fff",
        transform: "translateX(22px)",
        "& .MuiSwitch-thumb:before": {
          backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
            "#fff"
          )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
        },
        "& + .MuiSwitch-track": {
          opacity: 1,
          backgroundColor:
            theme.palette.mode === "dark" ? "#8796A5" : "#aab4be",
        },
      },
    },
    "& .MuiSwitch-thumb": {
      backgroundColor: theme.palette.mode === "dark" ? "#003892" : "#001e3c",
      width: 30,
      height: 32,
      "&::before": {
        content: "''",
        position: "absolute",
        width: "100%",
        height: "100%",
        left: 0,
        top: 0,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          "#fff"
        )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
      },
    },
    "& .MuiSwitch-track": {
      opacity: 1,
      backgroundColor: theme.palette.mode === "dark" ? "#8796A5" : "#aab4be",
      borderRadius: 20 / 2,
    },
  },

  Muiswitch_Navbar: {
    width: 72,
    height: 44,

    // marginLeft: 0.5,
    // position: "fixed",
    zIndex: 99999,
    // top: "94%",

    "& .MuiSwitch-switchBase": {
      margin: 1,
      padding: 0,
      transform: "translateX(6px)",
      "&.Mui-checked": {
        color: "#fff",
        transform: "translateX(22px)",
        "& .MuiSwitch-thumb:before": {
          backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
            "#fff"
          )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
        },
        "& + .MuiSwitch-track": {
          opacity: 1,
          backgroundColor:
            theme.palette.mode === "dark" ? "#8796A5" : "#aab4be",
        },
      },
    },
    "& .MuiSwitch-thumb": {
      backgroundColor: theme.palette.mode === "dark" ? "#003892" : "#001e3c",
      width: 30,
      height: 32,
      "&::before": {
        content: "''",
        position: "absolute",
        width: "100%",
        height: "100%",
        left: 0,
        top: 0,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          "#fff"
        )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
      },
    },
    "& .MuiSwitch-track": {
      opacity: 1,
      backgroundColor: theme.palette.mode === "dark" ? "#8796A5" : "#aab4be",
      borderRadius: 20 / 2,
    },
  },
  //sidebar
  sb_sideiconButton: {
    color: theme.palette.primary.main,
    gap: 1,

    // "@media (max-width: 600px)": {
    //   width: "100%",
    //   "&:hover": {
    //     width: "200%",
    //   },
    // },
    borderRadius: "8px",
    "&:hover": {
      color: theme.palette.text.buttontxt,
      backgroundColor: theme.palette.primary.main,
      borderRadius: 1,
    },
  },

  sb_sideiconButton_up_down: {
    padding: "4px 0",
    color: theme.palette.primary.main,
    width: "80%",
    borderRadius: "8px",
    "&:hover": {
      color: theme.palette.text.buttontxt,
      backgroundColor: theme.palette.primary.main,
      borderRadius: 1,
    },
  },
  sb_sideicon: {
    fontSize: 25,

    color: theme.palette.primary.main,
    "&:hover": {
      color: "White",
    },
  },

  sb_sideicon1: {
    fontSize: 25,

    color: theme.palette.primary.main,
  },
  sb_heatPumpIcon: {
    fontSize: 40,
    // ml: 2,
    mt: 1,

    color: theme.palette.primary.main,
  },
  sb_sidemenu: {
    marginLeft: 3,
    "& .MuiList-root": {
      paddingTop: 0,
      paddingBottom: 0,
    },
    "& .MuiPaper-root": {
      // boxShadow: `0px 1px 3px ${theme.palette.stroke.main}`,
      borderRadius: "4px",
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
      borderRadius: "8px",
      marginTop: "8px",
      overflow: "hidden",
      p: 1,
    },
  },

  sb_sidemenu_menuitem: {
    // boxShadow: `0px 0px 2px 0px ${theme.palette.input_field.fill}`,
    "&:hover": {
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0px 0px 2px 0px ${theme.palette.input_field.fill}`,
    },
    // padding: "12px 16px",
    // borderRadius: "0px",
    transition: "box-shadow 0.3s ease",
    // borderBottom: `0.4px solid ${theme.palette.primary.main}`,
    fontSize: "14px",
    padding: "10px 16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  sb_box: {
    borderTop: `2px solid ${theme.palette.background.default}`,
    borderBottom: `2px solid ${theme.palette.background.default}`,
    height: "60vh",
    overflowY: "auto",
    my: 1,
    position: "relative", // Required for pseudo-elements
    // paddingRight: "4px",
    // top: 6,
    // bottom: 6,

    // Firefox-specific scrollbar styles
    scrollbarWidth: "none", // Hide scrollbar in Firefox
    "-ms-overflow-style": "none", // Hide scrollbar in IE and Edge (legacy)

    // WebKit (Chrome, Edge, Safari) scrollbar styles
    "&::-webkit-scrollbar": {
      width: "0px", // Completely hides the scrollbar
    },

    // Show scrollbar when hovering
    "&:hover": {
      "&::-webkit-scrollbar": {
        width: "4px", // Make the scrollbar visible
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main, // Thumb color
        borderRadius: "2px", // Rounded corners
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: theme.palette.background.paper, // Track color
      },
    },
  },

  //Additems
  AI_box: {
    // backgroundColor: theme.palette.background.paper,
  },
  AI_Con: {
    // maxHeight: "360px",
    //overflowY: "auto",
  },
  AI_breadcrumbs: {
    alignItems: "center",
    // fontSize: 14,
    fontSize: { xs: 12, sm: 12, md: 14 },
    width: "50%",
  },
  AI_breadcrumbs2: {
    alignItems: "center",
    // fontSize: 14,
    fontSize: { xs: 12, sm: 12, md: 14 },
    mt: 1,
    // width: "50%",
    // display: { xs: "none", sm: "none", md: "block" },
  },
  AI_CustomAppBar: {
    background: theme.palette.background.paper,
    position: "fixed",
    boxShadow: 2,
    // height: "10vh",
    // zIndex: 10000,
    // py: 1,
    // boxShadow: "none",
  },
  AI_CustomAppBar1: {
    background: theme.palette.background.default,
    position: "fixed",

    // padding: 1,
    // height: "8vh",
    padding: { xs: 0, sm: 0, md: 1 },
    boxShadow: "none",
    // mt: 9,
    mt: { xs: 7, sm: 7, md: 9 },

    // height: "10vh",
  },


  box_container_add: {
    mt: { xs: 15, sm: 15, md: 18, lg: 18, xl: 18 },
    ml: { xs: 0, sm: 0, md: 13, lg: 13, xl: 13 },
    pr: { xs: 0, sm: 0, md: 4, lg: 4, xl: 4 },
  },

  AddStyledBoxX: {
    background: theme.palette.background.paper,
    // borderRadius: "4px",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    borderRadius: 2,
    mt: 2,
    // ml: 13,
    marginLeft: { xs: 0, sm: 0, md: 13, lg: 13, xl: 13 },
    // mr: 4,
    mr: { xs: 0, sm: 0, md: 4, lg: 4, xl: 4 },
    maxHeight: {
      md: "65vh",
      lg: "65vh",
      sx: "50vh",
      xs: "50vh",
    },
    overflowY: "auto",
    overflowX: "auto",

    "&::-webkit-scrollbar": {
      width: "0.2em",
    },

    "&::-webkit-scrollbar-0": {
      background: theme.palette.input_field.text,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.input_field.stroke,
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: theme.palette.background.tertiary,
    },
  },

  AI_container: {
    spacing: 2,
    // mt: 1,
    p: 2,
    // maxHeight: {
    //   md: "350px",
    //   lg: "448px",
    //   xs: "900px",
    // },

    alignContent: "flex-start",
    // minHeight: "60vh",
    minHeight: { xs: "auto", sm: "auto", md: "64vh", lg: "64vh", xl: "64vh" },
    overflowY: "auto",
    "::-webkit-scrollbar": { width: "2px" },
    "::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "1px",
    },
    "::-webkit-scrollbar-track": {
      backgroundColor: theme.palette.background.paper,
    },
  },

  cp_changepwd_container: {
    width: { xs: "90%", sm: "80%", md: "60%", lg: "40%" },

    overflowY: "auto",
    "::-webkit-scrollbar": { width: "2px" },
    "::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "1px",
    },
    "::-webkit-scrollbar-track": {
      backgroundColor: theme.palette.background.default,
    },
    backgroundColor: theme.palette.background.paper,

    borderRadius: "8px",
    // boxShadow: 1,
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    padding: { xs: 2, sm: 3, md: 4 },
    transition: "box-shadow 0.3s",
    "&:hover": {
      boxShadow: `0px 0px 8px 0px ${theme.palette.input_field.stroke}`,
    },
    // background: `linear-gradient(135deg, ${theme.palette.background.paper} 30%, ${theme.palette.background.default} 100%)`,
  },

  AI_AddStyledBox: {
    // boxShadow: "0px 0px 10px 0px #8484841A",
    background: theme.palette.background.default,
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    // mt: 18,
    padding: 0,
    // paddingTop: 1,
    mt: { xs: 16, sm: 16, md: 18, lg: 18, xl: 18 },
    ml: {
      xs: 0,
      sm: 0,
      md: 13,
      lg: 13,
      xl: 13,
    },
    borderRadius: 2,
    // position: { xs: "static", sm: "static", md: "relative" },
    // width: "92%",
    // ml: 13,
    // mr: 4,
    mr: {
      xs: 0,
      sm: 0,
      md: 4,
      lg: 4,
      xl: 4,
    },
  },

  OL_AddStyledBox: {
    mt: { xs: 18, sm: 18, md: 18, lg: 18, xl: 18 },
    ml: { xs: 0, sm: 0, md: 13 },
    mr: { xs: 0, sm: 0, md: 4 },
  },

  OL_AddStyledBox_container: {
    px: { xs: 2, sm: 2, md: 0 },
  },

  OL_vdg: { mt: { xs: -20, sm: -15, md: -15 } },

  cb_box_container: {
    // mt: 18,
    // ml: 13,
    // pr: 4,

    mt: { xs: 14, sm: 14, md: 18, lg: 18, xl: 18 },
    ml: { xs: 0, sm: 0, md: 11, lg: 11, xl: 11 },
    pr: { xs: 0, sm: 0, md: 2, lg: 2, xl: 2 },
  },

  cp_changepwd: {
    borderRadius: 2,
    mt: { xs: 16, sm: 16, md: 0, lg: 0, xl: 0 },
    display: "flex",
    justifyContent: "center",
    alignItems: { xs: "flex-start", sm: "flex-start", md: "center" },
    minHeight: "100vh",
    // width: { xs: "25%", sm: "25%", md: "100%" },
    padding: { xs: 2, sm: 3, md: 4 },
    // alignSelf: "center",
  },

  AddStyledBox2: {
    background: theme.palette.background.default,
    borderRadius: "4px",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,

    // borderRadius: 2,
  },

  AI_fixedStack: {
    position: "fixed",
    // right: "32px",
    // marginTop: {
    //   xs: -3,
    //   md: 0,
    //   lg: 0,
    // },

    right: {
      xs: "4px",
      sm: "4px",
      md: "32px",
      lg: "32px",
      xl: "32px",
    },

    marginTop: {
      // xs: -3,
      xs: -1,
      md: 0,
      lg: 0,
    },
    // background: theme.palette.background.default,
  },

  AI_fixedStack3: {
    position: "fixed",
    right: {
      xs: "4px",
      sm: "4px",
      md: "32px",
      lg: "32px",
      xl: "32px",
    },
    marginTop: {
      xs: -3,
      md: 0,
      lg: 0, // Default value
    },
  },
  AI_fixedStack1: {
    position: "fixed",
    right: {
      xs: "4px",
      sm: "4px",
      md: "24px",
      lg: "24px",
      xl: "24px",
    },
    marginTop: {
      xs: -1,
      sm: -6,
      md: 0,
      lg: 0, // Default value
    },
    // background: theme.palette.background.default,
  },

  AI_fixedStack2: {
    position: "fixed",
    // right: "25px",
    right: {
      xs: "4px",
      sm: "4px",
      md: "32px",
      lg: "32px",
      xl: "32px",
    },
    marginTop: {
      // xs: -3,
      xs: -1,
      md: 0,
      lg: 0, // Default value
    },
    // background: theme.palette.background.default,
  },
  VI_ViewStyledPaperX: {
    m: 1,
    boxShadow: "0px 0px 10px 0px #8484841A",
    maxHeight: {
      md: "60vh",
      lg: "60vh",
      sm: "50vh",
      xs: "50vh",
    },
    overflowY: "auto",
    overflowX: "scroll",
    maxWidth: {
      md: "96%",
      lg: "98%",
      sm: "96%",
      xs: "90%",
    },
    "&::-webkit-scrollbar": {
      width: "0.3em",
      height: "0.3em",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: theme.palette.primary.main,
    },

    // setupmenu
    VisuallyHiddenInput: {
      clip: "rect(0 0 0 0)",
      clipPath: "inset(50%)",
      height: 1,
      overflow: "hidden",
      position: "absolute",
      bottom: 0,
      left: 0,
      whiteSpace: "nowrap",
      width: 1,
      background: theme.palette.primary.main,
      color: theme.palette.text.buttontxt,
      textTransform: "capitalize",
      letterSpacing: "2px",
    },
  },

  //viewitem
  AI_headtableCell: {
    // position: "sticky",
    padding: 1,
    background: theme.palette.background.tertiary,
    boxShadow: `0px 0px 2px 0px ${theme.palette.input_field.stroke}`,
    // fontSize: "16px",
    fontSize: {
      xs: "14px",
      sm: "14px",
      md: "16px",
      lg: "16px",
      xl: "16px",
    },
    fontWeight: 700,
    lineHeight: "27.24px",
  },
  AI_headtableCellX: {
    position: "fixed",
    padding: 1,
    background: theme.palette.background.paper,
    fontSize: "15px",
    fontWeight: 700,
    lineHeight: "27.24px",
  },

  AI_tablecell: {
    background: theme.palette.background.fill,
    // padding: 1,
  },
  AI_tablecellX: {
    background: theme.palette.background.paper,
    p: 1,
    width: "100px",
    position: "fixed",
    right: 0,
    zIndex: 1,
    textAlign: "center",
  },

  AI_tablecell1: {
    textAlign: "right",
  },
  AI_tablecell2: {
    textAlign: "right",
    padding: 0,
  },

  AI_H: {
    background: theme.palette.primary.main,
  },

  AI_buttonx: {
    textTransform: "capitalize",
    letterSpacing: "1px",
    width: "120px",
    position: "flex-end",
    lineHeight: "1",
    fontSize: "12",
  },

  AI_button1: {
    textTransform: "capitalize",
    letterSpacing: "1px",
    width: "80px",
    position: "flex-end",
  },

  AI_button2: {
    textTransform: "capitalize",
    letterSpacing: "1px",
    width: "80px",
    color: theme.palette.text.buttontxt,
  },

  AI_HeaderContainer: {
    background: theme.palette.background.default,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",

    // marginTop: 0,
    marginLeft: { xs: 0, sm: 0, md: 11, lg: 11, xl: 11 },
    marginTop: { xs: 0, sm: 0, md: -1 },

    padding: "10px",
  },
  AI_HeaderContainer1: {
    // background: theme.palette.background.default,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    // marginTop: -1,
    // marginLeft: 11,
    // height: "16vh",
    marginLeft: { xs: 0, sm: 0, md: 11, lg: 11, xl: 11 },
    marginTop: { xs: 0, sm: 0, md: -1 },
    padding: "10px",
  },

  officer_AddStyledBox: {
    mt: {
      xs: 6,
      sm: 6,
      md: 18,
      lg: 18,
      xl: 18,
    },
    ml: {
      xs: 0,
      sm: 0,
      md: 13,
      lg: 13,
      xl: 13,
    },
    borderRadius: 2,
    mr: {
      xs: 0,
      sm: 0,
      md: 4,
      lg: 4,
      xl: 4,
    },
  },

  VI_menu: {
    marginTop: "-9px",
    marginLeft: "20px",
    color: theme.palette.background.paper,
  },

  VI_SearchField: {
    display: "flex",
    justifyContent: "space-between",

    width: {
      xs: "200px",
      sm: "280px",
      md: "320px",
    },
    "& .MuiOutlinedInput-root": {
      paddingLeft: "5px",
      borderRadius: "8px",
    },
  },
  VI_ViewStyledPaper: {
    m: 2,
    boxShadow: "0px 0px 10px 0px #8484841A",
    maxHeight: {
      md: "60vh",
      lg: "60vh",
      sm: "50vh",
      xs: "50vh",
    },
    overflowY: "auto",
    overflowX: "auto",
    maxWidth: {
      md: "96%",
      lg: "98%",
      sm: "96%",
      xs: "90%",
    },
    "&::-webkit-scrollbar": {
      width: "0.3em",
      height: "0.3em",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: theme.palette.primary.main,
    },

    // setupmenu
    VisuallyHiddenInput: {
      clip: "rect(0 0 0 0)",
      clipPath: "inset(50%)",
      height: 1,
      overflow: "hidden",
      position: "absolute",
      bottom: 0,
      left: 0,
      whiteSpace: "nowrap",
      width: 1,
      background: theme.palette.primary.main,
      color: theme.palette.text.buttontxt,
      textTransform: "capitalize",
      letterSpacing: "2px",
    },
  },
  VI_ViewStyledPaper2: {
    m: 1,
    boxShadow: "0px 0px 10px 0px #8484841A",
    maxHeight: {
      md: "60vh",
      lg: "60vh",
      sm: "50vh",
      xs: "50vh",
    },
    overflowY: "auto",
    overflowX: "auto",
    maxWidth: {
      md: "96%",
      lg: "96%",
      sm: "96%",
      xs: "90%",
    },
    "&::-webkit-scrollbar": {
      width: "0.3em",
      height: "0.3em",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: theme.palette.primary.main,
    },
  },

  // stock page start
  st_page_aside: {
    borderRadius: "4px",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    paddingTop: 1,
    // mt: 2,
    background: theme.palette.background.default,
    // ml: 13,
    ml: { xs: 0, sm: 0, md: 13 },
    mt: { xs: 16, sm: 16, md: 18, lg: 18, xl: 18 },
    // mt: 18,
    width: {
      xs: "100%", // for extra-small screens
      sm: "100%",
      md: "86%",
      lg: "92.5%",

      // for small screens
    },
  },

  officer_total_orders: {
    borderRadius: "4px",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    paddingTop: 1,
    background: theme.palette.background.default,
  },

  st_page_aside1: {
    borderRadius: "4px",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    paddingTop: 1,
    mt: 2,
    background: theme.palette.background.default,
    ml: {
      lg: "-8px",
      md: "-8px",

      sm: "130px",
    },
    mt: 0.5,
    width: {
      xs: "87%", // for extra-small screens
      sm: "78%",
      md: "85%",
      lg: "92%",

      // for small screens
    },
  },
  st_page_table: {
    borderRadius: "4px",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    maxHeight: {
      md: "70vh",
      lg: "70vh",
      sm: "50vh",
      xs: "50vh",
    },
    overflowY: "auto",
    overflowX: "auto",
    "&::-webkit-scrollbar": {
      width: "0.3em",
      height: "0.3em",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: theme.palette.primary.main,
    },

    background: theme.palette.background.default,
  },

  popup_title: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  popup_content: {
    backgroundColor: theme.palette.background.default,
  },

  stock_details_card: {
    p: 2,
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    backgroundColor: theme.palette.background.paper,
  },
  input_fields_icon_color: { color: theme.palette.text.secondary },
  // stock page end

  // Guest page start
  guest_card: {
    position: "relative",
    Width: "100%",

    padding: "0 0 8px 0",
    borderRadius: "8px",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,

    backgroundColor: theme.palette.background.fill,
    border: "1px solid ${theme.palette.input_field.stroke}",
  },

  guest_item_title: {
    display: "flex",
    justifyContent: "space-between",
    padding: "1em 3em 0 3em",
    alignItems: "center",
  },

  guest_cart_title: {
    display: "flex",
    justifyContent: "flex-start",
    // padding: "1em 1em 0 1em",
    alignItems: "start",
    background: theme.palette.background.default,
    // zIndex: 99,
    width: "100%",
    px: 1,
    py: 2,
    pb: 4,
    // borderBottom: `1px solid ${theme.palette.input_field.stroke}`,
  },

  guest_card_media_container: {
    width: "100%",
    height: "100%",
    // display: "flex",
  },

  guest_checkout_card_media: {
    width: 80,
    height: 80,
    marginRight: 2,
    borderRadius: "8px",
  },

  guest_checkout_card_container: {
    display: "flex",
    alignItems: "center",
    // border: "1px solid #ccc",
    border: "1px solid ${theme.palette.input_field.stroke}",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    borderRadius: "8px",
    // p: 2,
  },

  guest_card_media: {
    borderRadius: "2px",
    // padding: 2,
    backgroundColor: theme.palette.background.tertiary,
    width: "100%",
    height: "140px",
    alignSelf: "center",
    marginLeft: "auto",
    marginRight: "auto",
  },

  guest_list_card_media_container: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    // justifyContent: "flex-start",
    alignItems: "start",
    padding: "0.4rem",
    pl: 2,
  },

  guest_checkout_list_item: {
    backgroundColor: theme.palette.background.fill,
    border: "1px solid ${theme.palette.input_field.stroke}",

    borderRadius: "8px",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    // padding: "0 0 8px 0",
  },

  guest_list_card_media: {
    borderRadius: 2,
    // position: "relative",
    // left: 50,
    // padding: 2,
    // backgroundColor: theme.palette.background.tertiary,
    width: "100px",
    height: "100px",
    // p: 1,

    backgroundColor: theme.palette.background.fill,
    border: "1px solid ${theme.palette.input_field.stroke}",

    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
  },

  center_align: {
    display: "flex",
    justifyContent: "center",
    alignSelf: "center",
  },

  guest_card_content: {
    width: "100%",
    padding: "0.8rem 0.8rem 0.4rem 0.8rem",
  },

  guest_grid_card_content: {
    // width: "100%",
    pl: "0.8rem",
    justifyContent: "space-between",
    alignItems: "center",
    display: "flex",
  },

  guest_list_card_content: {
    display: "flex",
    // flexDirection: "column",
    width: "100%",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: "0.4rem",
    backgroundColor: theme.palette.background.fill,
    border: "1px solid ${theme.palette.input_field.stroke}",
    borderRadius: 2,
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    // pl: 6,
  },

  guest_card_texts: {
    color: theme.palette.text.primary,
    fontSize: "0.8rem",
    fontWeight: 500,
    lineHeight: "130%",
  },

  guest_card_item_price: {
    color: theme.palette.text.tertiary,
    fontSize: "1rem",
    fontWeight: 600,
    lineHeight: "130%",
  },

  guest_btn_add_to_cart: {
    // position: "absolute",
    right: 8,
    // top: 4,
    borderRadius: 1,
    color: theme.palette.system.warning,
    backgroundColor: theme.palette.background.default,
    boxShadow: `0px 0px 2px 0px ${theme.palette.input_field.stroke}`,
  },
  highlight: {
    // backgroundColor: theme.palette.primary.light,
    // color: theme.palette.primary.contrastText,
    fontWeight: "bold", // Optionally make it bold to emphasize
    borderRadius: "4px",
    fontSize: "18px",
  },

  highlight_red: {
    // backgroundColor: theme.palette.primary.light,
    color: theme.palette.system.danger,
    fontWeight: "bold", // Optionally make it bold to emphasize
    borderRadius: "4px",
    fontSize: "18px",
  },
  guest_btn_list_add_to_cart: {
    display: "flex",
    // flexDirection: "column",
    justifyContent: "center",
    alignSelf: "center",
    color: theme.palette.system.warning,
    backgroundColor: theme.palette.background.paper,
    // right: 20,
    borderRadius: 1,
    backgroundColor: theme.palette.background.fill,
    border: "1px solid ${theme.palette.input_field.stroke}",
    fontSize: "14px",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
  },

  guest_category_card_media: {
    padding: 1,
    width: "100%",
    height: "100%",
  },
  guest_category_card_container: {
    padding: 2,
    backgroundColor: theme.palette.background.tertiary,
    borderRadius: "8px",
    width: "100px",
    // height: "100%",
    height: "100px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  guest_category_card: {
    width: "100%",
    height: "100%",
    // display: "flex",
    // flexDirection: "column",
    alignItems: "center",
  },

  guest_category_card_text: {
    color: theme.palette.system.warning,
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: "normal",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  guest_category_card_popup: {
    backgroundColor: theme.palette.background.paper,
    padding: "16px 12px 16px 12px",
    borderRadius: "8px",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
  },
  guest_category_close_icon: {
    position: "absolute",
    top: 0,
    right: 0,
    backgroundColor: theme.palette.background.tertiary,
  },

  guest_card_list: {
    display: "flex",
    justifyContent: "start",
    alignItems: "center",

    // padding: "0 0 8px 0",
  },

  guest_footer: {
    p: 4,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    // backgroundColor: theme.palette.background.tertiary,
    // boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    borderRadius: 2,
  },

  guest_footer_logo: {
    p: 2,
    // height: "100%",
    // Width: "100%",

    maxHeight: "5px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  guest_footer_text: {
    color: theme.palette.text.primary,
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "130%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    flexDirection: "column",
  },

  guest_search: {
    margin: "1em",
    display: "flex",
    alignItems: "center",
    gap: 1,
    position: "relative",
  },

  guest_category_menu: {
    display: "flex",
    textAlign: "center",
    padding: 2,
    height: "2rem",
    cursor: "pointer",
    border: "1px solid",
    borderColor: theme.palette.system.warning,
    borderRadius: "8px",
    justifyContent: "center",
    alignItems: "center",
    // backgroundColor: theme.palette.system.warning,
    color: theme.palette.system.warning,
  },

  guest_btn_close: {
    zIndex: "1000",
    position: "absolute",
    top: 0,
    right: 8,
    backgroundColor: theme.palette.background.default,
  },

  guest_category_dialog: {
    padding: 2,
    backgroundColor: theme.palette.background.tertiary,
    borderRadius: 3,
  },

  // Guest page end

  // Group Access start
  group_access: {
    display: "flex",
    // spacing: 2,
    mt: 1,
    p: 2,

    overflowY: "auto",
    "::-webkit-scrollbar": { width: "8px" },
    "::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "4px",
    },
    "::-webkit-scrollbar-track": {
      backgroundColor: theme.palette.background.paper,
    },
  },

  group_access_container: {
    boxShadow: "0px 0px 10px 0px #8484841A",
    spacing: 4,
    borderRadius: 2,
    background: theme.palette.background.fill,
    display: "flex",
    alignItems: "flex-start",
    ml: 12,
    mr: 3,
  },

  col_1: {
    backgroundColor: theme.palette.background.paper,
    // boxShadow: "0px 0px 10px 0px #8484841A",
    // borderRadius: 2,
    // p: 2,
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
  },

  cb_col_1: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 1,
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
  },

  col_1_title: {
    padding: "12px 24px",
    backgroundColor: theme.palette.background.default,
    boxShadow: 1,
  },

  col_title: {
    padding: "16px 24px",
    backgroundColor: theme.palette.background.paper,
    // boxShadow: `0px 0px 4px 0px ${theme.palette.background.tertiary}`,
    borderRadius: 1,
    boxShadow: 1,
  },

  cb_col_title: {
    padding: "16px 24px",
    backgroundColor: theme.palette.background.default,
    // boxShadow: `0px 0px 4px 0px ${theme.palette.background.tertiary}`,
    borderRadius: 1,
    boxShadow: 1,
  },

  box_container: {
    mt: 18,
    ml: 13,
    pr: 4,
    // mr: 4,
  },

  // bill format end

  pf_typography: {
    marginBottom: "8px",
    padding: 1,
    fontSize: 20,
  },

  // Order Success start
  order_success_container: {
    padding: 4,
    backgroundColor: theme.palette.background.paper,
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    // justifyContent: "center",
    // flexDirection: "column",
    // display: "flex",
    // alignItems: "center",
    // gap: 2,
    borderRadius: 2,
    alignItems: "center",
    transition: "box-shadow 0.3s",
    "&:hover": { boxShadow: 6 },
    background: `linear-gradient(135deg, ${theme.palette.background.default} 30%, ${theme.palette.background.paper} 100%)`,
  },
  order_success_order_nmb: {
    border: "1px solid ${theme.palette.primary.main}",
    boxShadow: `0px 0px 4px 0px ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    minHeight: "36px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 1,
    // width: "10%",
    minWidth: "48px",
    mt: 2,
    mb: 1,
  },
  // Order Success end

  col_1: {
    display: "flex",
    backgroundColor: theme.palette.background.paper,
    flexDirection: "column",
    alignItems: "center", // Center align horizontally
    justifyContent: "center", // Center align vertically
    textAlign: "center", // Center text alignment
    // height: '50vh', // Full viewport height to center content vertically
    ml: 2,
    padding: 2,
  },

  col_1_title: {
    padding: "12px 24px",
    backgroundColor: theme.palette.background.default,
    boxShadow: "0px 0px 10px 0px #8484841A",
    alignItems: "center",
    textAlign: "center",
  },
  AI_ordercancel: {
    mt: 20,
    width: "93%",
    ml: 12,
    border: `1px solid ${
      theme.palette.mode === "dark" ? "#252836" : "#D7D7D7"
    }`,
    boxShadow: "0px 0px 10px 0px #8484841A",
    borderRadius: 1,
    background: theme.palette.background.default,
    maxHeight: "70vh",
    "::-webkit-scrollbar": { width: "4px" },
    "::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "4px",
    },
    "::-webkit-scrollbar-track": {
      backgroundColor: theme.palette.background.paper,
    },
  },

  AI_fixedStack3: {
    position: "fixed",
    right: "80px",

    marginTop: {
      xs: -3,
      md: 0,
      lg: -2.5, // Default value
    },
  },

  // Login Style Start

  login_container: {
    position: "relative",
    width: "100vw",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "#f0e6ef",
    overflow: "hidden",
    backgroundColor: "#8EC5FC",
    backgroundImage: "linear-gradient(62deg, #8EC5FC 0%, #0077B6 100%)",
    height: "100vh",
    overflow: "hidden",
    position: "relative",
  },

  // shape1: {
  //   top: "10%",
  //   left: "20%",
  //   width: "100px",
  //   height: "100px",
  //   background: "#1E90FF",
  //   borderRadius: "50%",
  //   opacity: 0.5,
  //   animationDelay: "0s",
  // },

  shape1: {
    position: "absolute",
    top: "5%",
    left: "10%",
    width: "300px",
    height: "300px",
    background: "rgba(255, 255, 255, 0.2)",
    borderRadius: "50%",
    animation: "move 10s infinite",
    "@keyframes move": {
      "0%": { transform: "translate(0, 0)" },
      "50%": { transform: "translate(100px, 100px)" },
      "100%": { transform: "translate(0, 0)" },
    },
  },

  img_login_card: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: 2,
    // borderTopLeftRadius: "15px",
    // borderBottomLeftRadius: "15px",
    position: "relative",
    animation: "slideIn 3s ease-out forwards", // Add animation for sliding effect
    "@keyframes slideIn": {
      "0%": { transform: "translateX(-100%)" }, // Starts off-screen from left
      "100%": { transform: "translateX(0)" }, // Ends at the normal position
    },
  },

  img_lg_card_shape1: {
    width: "40%", // You can adjust the size as needed
    height: "40%", // You can adjust the size as needed
    objectFit: "cover", // Ensures the image covers the area while maintaining aspect ratio
    borderRadius: 2, // Optional: applies border radius if desired
    position: "absolute", // You can use absolute positioning for the image inside the container
    top: "50%", // Moves the image to the vertical center
    left: "50%", // Moves the image to the horizontal center
    transform: "translate(-50%, -50%)", // Offsets the image to perfectly center it
    display: "flex",
    justifyContent: "center", // Ensures the content is horizontally centered
    alignItems: "center", // Ensures the content is vertically centered
  },

  os_img_confirm: {
    width: "20%",
    // height: "25%",
    objectFit: "cover",
    borderTopLeftRadius: "15px",
    borderBottomLeftRadius: "15px",
    position: "relative",
  },

  shape2: {
    position: "absolute",
    top: "60%",
    right: "15%",
    width: "200px",
    height: "200px",
    background: "rgba(255, 255, 255, 0.2)",
    borderRadius: "50%",
    animation: "move 15s infinite",
    "@keyframes move": {
      "0%": { transform: "translate(0, 0)" },
      "50%": { transform: "translate(-50px, -50px)" },
      "100%": { transform: "translate(0, 0)" },
    },
  },

  login_card_container: {
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    borderRadius: "0",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
    maxWidth: "900px",
    margin: "auto",
    zIndex: 1,
    display: "flex",
    overflow: "hidden",
  },
  login_card_left: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#e6f0ff",
    // borderTopLeftRadius: "15px",
    // borderBottomLeftRadius: "15px",
    padding: "20px",
    overflow: "hidden",
  },
  login_card_right: {
    padding: "40px",
  },
  login_btn: {
    // textTransform: "capitalize",
    // backgroundColor: "#1E90FF",
    // color: "#fff",
    // "&:hover": {
    //   backgroundColor: "#1C86EE",
    // },
    // transition: "background-color 0.3s ease, transform 0.3s ease",
    // "&:hover": {
    //   backgroundColor: "#1C86EE",
    //   transform: "scale(1.05)",
    // },
    width: "35%",
    position: "relative",
    margin: 0,
    padding: "0.4em 1em",
    outline: "none",
    textDecoration: "none",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    border: "none",
    textTransform: "capitalize",
    backgroundColor: theme.palette.background.primary,
    borderRadius: 1,
    color: "#fff",
    fontWeight: 300,
    fontSize: "16px",
    zIndex: 0,
    overflow: "hidden",
    transition: "all 0.3s cubic-bezier(0.02, 0.01, 0.47, 1)",
    "&:hover": {
      animation: "sh0 0.5s ease-in-out both",
      "&::before, &::after": {
        opacity: 0.15,
        transform: "translate(50%, 0) scale(1.1)",
      },
    },
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      right: 0,
      bottom: 0,
      width: "100px",
      height: "100px",
      borderRadius: "50%",
      background: "#fff",
      opacity: 0,
      zIndex: -1,
      transform: "translate(100%, -25%) translate3d(0, 0, 0)",
      transition:
        "transform 0.15s cubic-bezier(0.02, 0.01, 0.47, 1), opacity 0.15s cubic-bezier(0.02, 0.01, 0.47, 1)",
    },
  },
  retro_text: {
    // fontFamily: "'Pacifico', cursive",
    color: "#1F1D2B",
  },

  login_retro_text: {
    // fontFamily: "'Pacifico', cursive",
    color: "#fff",
    fontSize: "10px",
  },
  form_input: {
    backgroundColor: "#e6f7ff",
    borderRadius: "5px",
  },
  forgot_password: {
    color: "#1E90FF",
  },
  //Login Style end

  // CashReceipt start
  cr_right_align: {
    display: "flex",
    justifyContent: "right",
    flexDirection: "column",
    textAlign: "right",
  },

  // cash receipt end

  // dashboard start

  db_root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    minHeight: "100vh",

    flexGrow: 1,
    height: "100%",
    overflow: "auto",
    // Width: "100%",

    ml: { xs: 0, sm: 0, md: 10 },
    position: "fixed",
    // bottom: 0,
    right: 0,
    left: 0,
    // mt: 10,
    mt: { xs: 6, sm: 6, md: 10 },
  },

  db_header: {
    marginBottom: theme.spacing(1),
    // borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(2),
  },
  db_headerTitle: {
    fontWeight: "bold",
    textAlign: "left",
  },
  db_mainContent: {
    flexGrow: 1,
  },
  db_card: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: theme.spacing(2),
    boxShadow: 2,
    // borderRadius: theme.shape.borderRadius,
    textAlign: "center",
    backgroundColor: "background.paper",
  },
  // dashboard start

  BR_box1: {
    borderRadius: "4px",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    Height: "250px",
  },
  BR_box: {
    p: 2,
    borderRadius: "4px",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
    maxHeight: {
      md: "60vh",
      lg: "62vh",
      xl: "62vh",
      sm: "70vh",
      xs: "50vh",
    },
    overflowY: "auto",
    overflowX: "auto",
    maxWidth: {
      md: "96%",
      lg: "98%",
      sm: "96%",
      xs: "90%",
    },
    "&::-webkit-scrollbar": {
      width: "0.3em",
      height: "0.8em",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: "2px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: theme.palette.primary.main,
    },
  },
  br_card_media: {
    width: "auto",
    maxWidth: "20px",
    minWidth: "100px",
    height: "100%",
    alignSelf: "center",
    padding: 1,
    marginLeft: "auto",
    marginRight: "auto",
    objectFit: "contain",
    background: theme.palette.background.default,
    borderRadius: "10%",
    boxShadow: `0px 0px 4px 0px ${theme.palette.input_field.stroke}`,
  },
});

export { lightTheme, darkTheme, useStyles };
